'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type Props = {
  children: React.ReactNode;
  onClick?: () => void;
  tooltipText: string;
  duration?: number; // optional: auto-hide delay (ms), default 1500
  showOnHover?: boolean;
  toolTipContainerClassName?: string;
};

export const TooltipedElem: React.FC<Props> = ({
  children,
  onClick,
  tooltipText,
  duration = 1500,
  showOnHover = false,
  toolTipContainerClassName = '',
}) => {
  const [open, setOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const pointerTypeRef = React.useRef<string>('mouse');

  const clearTimer = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const openWithTimer = React.useCallback(() => {
    clearTimer();
    setOpen(true);
    if (duration) {
      timeoutRef.current = setTimeout(() => setOpen(false), duration);
    }
  }, [clearTimer, duration]);

  const handleClick = React.useCallback(() => {
    onClick?.();

    if (!showOnHover) {
      openWithTimer();
      return;
    }

    if (pointerTypeRef.current === 'mouse') {
      return;
    }

    if (open) {
      clearTimer();
      setOpen(false);
      return;
    }

    openWithTimer();
  }, [onClick, showOnHover, open, openWithTimer, clearTimer]);

  React.useEffect(() => clearTimer, [clearTimer]);

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    pointerTypeRef.current = e.pointerType;
  }, []);

  const handlePointerEnter = React.useCallback(
    (e: React.PointerEvent) => {
      pointerTypeRef.current = e.pointerType;
      if (showOnHover && e.pointerType === 'mouse') {
        clearTimer();
        setOpen(true);
      }
    },
    [showOnHover, clearTimer],
  );

  const handlePointerLeave = React.useCallback(
    (e: React.PointerEvent) => {
      if (showOnHover && e.pointerType === 'mouse') {
        clearTimer();
        setOpen(false);
      }
    },
    [showOnHover, clearTimer],
  );

  return (
    <TooltipProvider>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <div
            onPointerDown={handlePointerDown}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onClick={handleClick}
            style={{ display: 'inline-block' }}
          >
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side='top'
          align='end'
          sideOffset={6}
          alignOffset={-10}
          className={cn('py-2 text-start text-pretty leading-[16px]', toolTipContainerClassName)}
        >
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
